// Base URL for API requests
const baseURL = import.meta.env.VITE_API_BASE_URL || '';

// Helper function to create authenticated fetch requests
const createAuthenticatedFetch = async (url: string, options: RequestInit = {}) => {
  const token = localStorage.getItem('token');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${baseURL}${url}`, {
    ...options,
    headers,
    credentials: 'include', // Include cookies for Bull Board access
  });

  // If unauthorized, try to refresh token once
  if (response.status === 401 && !url.includes('/auth/')) {
    const refreshToken = localStorage.getItem('refreshToken');
    if (refreshToken) {
      try {
        // Try to refresh the token
        const refreshResponse = await fetch(`${baseURL}/auth/refresh-token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
          credentials: 'include',
        });

        if (refreshResponse.ok) {
          const refreshData = await refreshResponse.json();
          localStorage.setItem('token', refreshData.token);
          localStorage.setItem('refreshToken', refreshData.refreshToken);
          localStorage.setItem('user', JSON.stringify(refreshData.user));

          // Retry the original request with new token
          headers['Authorization'] = `Bearer ${refreshData.token}`;
          return fetch(`${baseURL}${url}`, {
            ...options,
            headers,
            credentials: 'include',
          });
        }
      } catch (error) {
        console.error('Token refresh failed:', error);
      }
    }

    // If refresh failed, dispatch token expired event
    window.dispatchEvent(new CustomEvent('auth-token-expired', {
      detail: { reason: 'Authentication failed' }
    }));
  }

  return response;
};

// Enhanced error handling for API responses
const handleApiResponse = async (response: Response) => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage = errorData.error || errorData.message || response.statusText;
    throw new Error(`API Error (${response.status}): ${errorMessage}`);
  }
  return response.json();
};

// Type definitions matching OpenAPI specification
export type UserRole = 'super_admin' | 'expert' | 'admin' | 'user';
export type UserStatus = 'pending' | 'approved' | 'disabled' | 'rejected';

export type UserType = 'employee' | 'distributor' | 'customer';
export type User = {
  id: string;
  name?: string; // User's name from database
  displayName: string; // Maps to displayName from domain entity
  phone: string; // Maps to phoneNumber from domain entity
  email?: string;
  organization?: string; // Organization details for identifying distributors/customers
  roles: UserRole[];
  status: UserStatus;
  userType?: UserType;
  createdAt: string;
  updatedAt?: string;
};

export type ConversationStatus = 'active' | 'archived' | 'deleted';

export type ConversationMessage = {
  id: string;
  conversationId: string;
  content: string;
  messageType: 'user' | 'assistant' | 'expert';
  channel: 'whatsapp' | 'sms' | 'web';
  metadata?: Record<string, any>;
  confidenceScore?: number;
  userId?: string;
  createdAt: string;
  updatedAt?: string;
};

export type Conversation = {
  id: string;
  title: string;
  status: ConversationStatus;
  userId: string;
  isRead: boolean;
  messageCount: number;
  lastMessageAt?: string;
  lastActivityAt: string;
  createdAt: string;
  updatedAt: string;
  metadata?: Record<string, any>;
};

export type PaginationInfo = {
  limit: number;
  offset: number;
  total: number;
  hasMore: boolean;
};

// ========================================
// EXPERT ESCALATION TYPES
// ========================================

export type EscalationType = 'manual' | 'admin_initiated';
export type EscalationPriority = 'low' | 'normal' | 'high' | 'urgent';
export type EscalationStatus = 'pending' | 'in_progress' | 'resolved';
export type AssignmentStatus = 'assigned' | 'claimed' | 'resolved';

export type Escalation = {
  id: string;
  conversationId: string;
  messageId: string;
  userId: string;
  type: EscalationType;
  priority: EscalationPriority;
  status: EscalationStatus;
  reason?: string;
  escalatedAt: string;
  resolvedAt?: string;
  assignedExpertId?: string;
  resolvedBy?: string;
  responseTimeMinutes?: number;
  confidenceScore?: number;
  resolutionNotes?: string;
  createdAt: string;
  updatedAt: string;
  // Associations
  conversation?: Conversation;
  message?: ConversationMessage;
  user?: User;
  assignedExpert?: User;
  resolver?: User;
};

export type ExpertAssignment = {
  id: string;
  conversationId: string;
  escalationId: string;
  expertId: string;
  status: AssignmentStatus;
  assignedAt: string;
  claimedAt?: string;
  resolvedAt?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  // Associations
  conversation?: Conversation;
  escalation?: Escalation;
  expert?: User;
};

export type EscalationWithDetails = Escalation & {
  conversation: Conversation;
  message: ConversationMessage;
  user: User;
  assignedExpert?: User;
  assignment?: ExpertAssignment;
};

export type EscalationListResponse = {
  escalations: EscalationWithDetails[];
  pagination: PaginationInfo;
};

export type ExpertMetrics = {
  totalEscalations: number;
  pendingCount: number;
  inProgressCount: number;
  resolvedCount: number;
  averageResponseTimeMinutes: number;
  expertPerformance: {
    expertId: string;
    expertName: string;
    totalResolved: number;
    averageResponseTime: number;
  }[];
};

export type ConversationResponse = {
  id: string;
  title: string;
  status: ConversationStatus;
  userId: string;
  isRead: boolean;
  messageCount: number;
  lastMessageAt?: string;
  lastActivityAt: string;
  createdAt: string;
  updatedAt: string;
  metadata?: Record<string, any>;
  user?: User;
};

export type ConversationListResponse = {
  conversations: ConversationResponse[];
  pagination: PaginationInfo;
  user?: User;
};

export type MessageListResponse = {
  messages: ConversationMessage[];
  pagination: PaginationInfo;
  conversation?: Conversation;
};

// Type alias for compatibility with existing components
export type ChatMessage = ConversationMessage;

// Shared date-range + admin-exclusion options for the analytics endpoints.
// Prefer startDate/endDate (custom range) over days when both are present.
export type AnalyticsRangeOptions = {
  days?: number;
  startDate?: string; // yyyy-mm-dd
  endDate?: string; // yyyy-mm-dd
  excludeAdmins?: boolean;
};

export type UserAnalyticsRow = {
  id: string;
  name: string;
  email: string | null;
  roles: UserRole[];
  conversations: number;
  messages: number;
  lastActive: string | null;
  joinedAt: string;
};

const buildAnalyticsQuery = (options: AnalyticsRangeOptions): string => {
  const params = new URLSearchParams();
  if (options.startDate && options.endDate) {
    params.set('startDate', options.startDate);
    params.set('endDate', options.endDate);
  } else {
    params.set('days', String(options.days ?? 7));
  }
  if (options.excludeAdmins) params.set('excludeAdmins', 'true');
  return params.toString();
};

export const api = {
  // ========================================
  // USER MANAGEMENT APIs
  // ========================================

  /**
   * Get all users (Admin only)
   * GET /users
   */
  getUsers: async (status?: UserStatus, userType?: UserType): Promise<User[]> => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (userType) params.append('userType', userType);

    const response = await createAuthenticatedFetch(`/users${params.toString() ? `?${params}` : ''}`);
    return handleApiResponse(response);
  },

  /**
   * Update user status (Admin only)
   * PATCH /users/{id}/status
   */
  updateUserStatus: async (id: string, status: UserStatus, userType?: UserType, rejectReason?: string): Promise<User> => {
    const body: { status: UserStatus; userType?: UserType, rejectReason?: string } = { status };
    if (userType !== undefined) {
      body.userType = userType;
    }
    if (rejectReason !== undefined) {
      body.rejectReason = rejectReason;
    }
    const response = await createAuthenticatedFetch(`/users/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
    return handleApiResponse(response);
  },

  /**
   * Delete a user (Admin/Super Admin only)
   * DELETE /admin/users/delete/{id}
   */
  deleteUser: async (id: string): Promise<{ message: string }> => {
    const response = await createAuthenticatedFetch(`/admin/users/delete/${id}`, {
      method: 'DELETE',
    });
    return handleApiResponse(response);
  },

  /**
   * Create a new user with roles (Admin/Super Admin only)
   * POST /admin/users
   * Admins can assign: user, expert
   * Super admins can assign: user, expert, admin, super_admin
   */
  createUser: async (data: {
    name: string;
    organization: string;
    email: string;
    password: string;
    roles: UserRole[];
    userType?: UserType;
  }): Promise<{ message: string; user: User }> => {
    const response = await createAuthenticatedFetch('/admin/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return handleApiResponse(response);
  },

  /**
   * Update user roles (Super Admin only)
   * PATCH /admin/users/{id}/roles
   */
  // updateUserRoles: async (id: string, roles: UserRole[]): Promise<{ message: string; user: User }> => {
  //   const response = await createAuthenticatedFetch(`/admin/users/${id}/roles`, {
  //     method: 'PATCH',
  //     body: JSON.stringify({ roles }),
  //   });
  //   return handleApiResponse(response);
  // },

  /**
   * Update user (Super Admin only)
   * PATCH /admin/users/{id}/roles
   */
  updateUser: async (id: string, data: { name?: string; phone?: string; email?: string; organization?: string; roles?: UserRole[]; userType?: UserType }): Promise<{ message: string; user: User }> => {
    const response = await createAuthenticatedFetch(`/admin/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    return handleApiResponse(response);
  },

  // ========================================
  // ADMIN CONVERSATION MANAGEMENT APIs
  // ========================================

  /**
   * Get conversations for a specific user (Admin only)
   * GET /admin/users/{userId}/conversations
   */
  getUserConversations: async (userId: string, options?: {
    status?: ConversationStatus;
    limit?: number;
    offset?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<ConversationListResponse> => {
    const params = new URLSearchParams();
    if (options?.status) params.append('status', options.status);
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());
    if (options?.sortBy) params.append('sortBy', options.sortBy);
    if (options?.sortOrder) params.append('sortOrder', options.sortOrder);

    const response = await createAuthenticatedFetch(
      `/admin/users/${userId}/conversations${params.toString() ? `?${params}` : ''}`
    );
    return handleApiResponse(response);
  },

  /**
   * Get specific conversation (Admin only)
   * GET /admin/conversations/{id}
   */
  getConversation: async (conversationId: string): Promise<ConversationResponse> => {
    const response = await createAuthenticatedFetch(`/admin/conversations/${conversationId}`);
    return handleApiResponse(response);
  },

  /**
   * Get messages in a conversation (Admin only)
   * GET /admin/conversations/{id}/messages
   */
  getConversationMessages: async (
    conversationId: string,
    options?: {
      limit?: number;
      offset?: number;
      messageType?: string;
      search?: string;
      startDate?: string;
      endDate?: string;
    }
  ): Promise<MessageListResponse> => {
    const params = new URLSearchParams();
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());
    if (options?.messageType) params.append('messageType', options.messageType);
    if (options?.search) params.append('search', options.search);
    if (options?.startDate) params.append('startDate', options.startDate);
    if (options?.endDate) params.append('endDate', options.endDate);

    const response = await createAuthenticatedFetch(
      `/admin/conversations/${conversationId}/messages${params.toString() ? `?${params}` : ''}`
    );
    return handleApiResponse(response);
  },

  /**
   * Send expert message to conversation (Admin only)
   * POST /admin/conversations/{id}/messages
   */
  sendExpertMessage: async (
    conversationId: string,
    content: string,
    metadata?: Record<string, any>
  ): Promise<ConversationMessage> => {
    const response = await createAuthenticatedFetch(`/admin/conversations/${conversationId}/messages`, {
      method: 'POST',
      body: JSON.stringify({
        content,
        messageType: 'expert',
        metadata
      }),
    });
    return handleApiResponse(response);
  },

  /**
   * Clear all messages in conversation (Admin only)
   * DELETE /admin/conversations/{id}/messages
   */
  clearConversationMessages: async (conversationId: string): Promise<{ success: boolean; deletedCount: number }> => {
    const response = await createAuthenticatedFetch(`/admin/conversations/${conversationId}/messages`, {
      method: 'DELETE',
    });
    return handleApiResponse(response);
  },

  // ========================================
  // AUTHENTICATION APIs
  // ========================================

  /**
   * Request OTP for login
   * POST /auth/request-otp
   */
  requestOTP: async (identifier: string): Promise<{
    success: boolean;
    message: string;
    sessionId: string;
    expiresAt: string;
  }> => {
    const response = await fetch(`${baseURL}/auth/request-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ identifier }),
    });
    return handleApiResponse(response);
  },

  /**
   * Verify OTP and get auth token
   * POST /auth/verify-otp
   */
  verifyOTP: async (sessionId: string, otp: string): Promise<{
    token: string;
    refreshToken: string;
    expiresIn: number;
    user: User;
  }> => {
    const response = await fetch(`${baseURL}/auth/verify-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId, otp }),
    });
    return handleApiResponse(response);
  },

  /**
   * Refresh authentication token
   * POST /auth/refresh-token
   */
  refreshToken: async (refreshToken: string): Promise<{
    token: string;
    refreshToken: string;
    expiresIn: number;
    user: User;
  }> => {
    const response = await fetch(`${baseURL}/auth/refresh-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
    return handleApiResponse(response);
  },

  /**
   * Logout (invalidate tokens)
   * POST /auth/logout
   */
  logout: async (): Promise<{ success: boolean; message: string }> => {
    const response = await createAuthenticatedFetch('/auth/logout', {
      method: 'POST',
    });
    return handleApiResponse(response);
  },

  /**
   * Get current user profile
   * GET /users/me (or similar endpoint)
   */
  getCurrentUser: async (): Promise<User> => {
    const response = await createAuthenticatedFetch('/users/me');
    return handleApiResponse(response);
  },

  /**
   * Login with email and password
   * POST /auth/login
   */
  loginWithPassword: async (email: string, password: string): Promise<{
    token: string;
    refreshToken: string;
    expiresIn: number;
    user: User;
  }> => {
    const response = await fetch(`${baseURL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    return handleApiResponse(response);
  },

  /**
   * Register admin with email and password
   * POST /auth/register
   */
  registerWithPassword: async (data: {
    email: string;
    password: string;
    displayName: string;
    phone?: string;
  }): Promise<{
    success: boolean;
    userId: string;
    sessionId?: string;
    message: string;
    verificationRequired?: boolean;
  }> => {
    const response = await fetch(`${baseURL}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return handleApiResponse(response);
  },

  // ========================================
  // EXPERT ESCALATION APIs
  // ========================================

  /**
   * Get expert escalations (Expert/Admin only)
   * GET /api/escalations
   */
  getEscalations: async (options?: {
    status?: EscalationStatus;
    priority?: EscalationPriority;
    assignedExpertId?: string;
    limit?: number;
    offset?: number;
  }): Promise<EscalationListResponse> => {
    const params = new URLSearchParams();
    if (options?.status) params.append('status', options.status);
    if (options?.priority) params.append('priority', options.priority);
    if (options?.assignedExpertId) params.append('assignedExpertId', options.assignedExpertId);
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());

    const response = await createAuthenticatedFetch(
      `/escalations${params.toString() ? `?${params}` : ''}`
    );
    return handleApiResponse(response);
  },

  /**
   * Get specific escalation details (Expert/Admin only)
   * GET /api/escalations/{id}
   */
  getEscalation: async (escalationId: string): Promise<EscalationWithDetails> => {
    const response = await createAuthenticatedFetch(`/escalations/${escalationId}`);
    return handleApiResponse(response);
  },

  /**
   * Claim an escalation (Expert only)
   * POST /api/escalations/{id}/claim
   */
  claimEscalation: async (escalationId: string): Promise<{
    escalation: Escalation;
    assignment: ExpertAssignment;
  }> => {
    const response = await createAuthenticatedFetch(`/escalations/${escalationId}/claim`, {
      method: 'POST',
    });
    return handleApiResponse(response);
  },

  /**
   * Resolve an escalation (Expert/Admin only)
   * POST /api/escalations/{id}/resolve
   */
  resolveEscalation: async (escalationId: string, resolutionNotes?: string): Promise<Escalation> => {
    const response = await createAuthenticatedFetch(`/escalations/${escalationId}/resolve`, {
      method: 'POST',
      body: JSON.stringify({ resolutionNotes }),
    });
    return handleApiResponse(response);
  },

  /**
   * Get expert metrics and analytics (Admin only)
   * GET /api/expert-analytics
   */
  getExpertMetrics: async (options?: {
    startDate?: string;
    endDate?: string;
    expertId?: string;
  }): Promise<ExpertMetrics> => {
    const params = new URLSearchParams();
    if (options?.startDate) params.append('startDate', options.startDate);
    if (options?.endDate) params.append('endDate', options.endDate);
    if (options?.expertId) params.append('expertId', options.expertId);

    const response = await createAuthenticatedFetch(
      `/expert-analytics${params.toString() ? `?${params}` : ''}`
    );
    return handleApiResponse(response);
  },

  /**
   * Get all experts (Admin only)
   * GET /api/experts
   */
  getExperts: async (status?: UserStatus): Promise<User[]> => {
    const params = new URLSearchParams();
    params.append('role', 'expert');
    if (status) params.append('status', status);

    const response = await createAuthenticatedFetch(`/users${params.toString() ? `?${params}` : ''}`);
    return handleApiResponse(response);
  },

  /**
   * Get chat history context for escalation (Expert only)
   * GET /api/escalations/{id}/chat-context
   * Returns messages before escalation (pre-escalation context)
   */
  getEscalationChatContext: async (escalationId: string, limit: number = 10): Promise<{
    messages: ConversationMessage[];
    conversation: Conversation;
  }> => {
    const response = await createAuthenticatedFetch(
      `/escalations/${escalationId}/chat-context?limit=${limit}`
    );
    return handleApiResponse(response);
  },

  /**
   * Get full escalation history including expert responses
   * GET /api/escalations/{id}/full-history
   * Returns all messages for the escalation conversation (useful for resolved escalations)
   */
  getEscalationFullHistory: async (escalationId: string): Promise<{
    messages: ConversationMessage[];
    conversation: Conversation;
  }> => {
    const response = await createAuthenticatedFetch(
      `/escalations/${escalationId}/full-history`
    );
    return handleApiResponse(response);
  },

  /**
   * Send expert response to user and auto-resolve escalation
   * POST /api/escalations/{id}/respond
   */
  sendExpertResponse: async (escalationId: string, message: string): Promise<{
    escalation: Escalation;
    messageQueued: boolean;
  }> => {
    const response = await createAuthenticatedFetch(
      `/escalations/${escalationId}/respond`,
      {
        method: 'POST',
        body: JSON.stringify({ message }),
      }
    );
    return handleApiResponse(response);
  },

  // ===== ANALYTICS (Admin/Super Admin) =====

  /**
   * Get conversation analytics
   * GET /api/analytics/conversations
   */
  getConversationAnalytics: async (options: AnalyticsRangeOptions = {}): Promise<{
    success: boolean;
    data: {
      summary: {
        totalConversations: number;
        activeConversations: number;
        totalMessages: number;
        messagesInPeriod: number;
        averageResponseTime: number;
      };
      trends: {
        messagesByDay: Array<{ date: string; count: number }>;
        conversationsByDay: Array<{ date: string; count: number }>;
        activeUsersByDay: Array<{ date: string; count: number }>;
        messagesByType: Array<{ type: string; count: number }>;
      };
      period: {
        days: number;
        startDate: string;
        endDate: string;
      };
    };
    timestamp: string;
  }> => {
    const response = await createAuthenticatedFetch(`/analytics/conversations?${buildAnalyticsQuery(options)}`);
    return handleApiResponse(response);
  },

  /**
   * Get system overview analytics
   * GET /api/analytics/overview
   *
   * `asOfDate` (yyyy-mm-dd) makes the user counts a cumulative snapshot as of
   * the end of that date, excluding users created after it — used when the
   * dashboard has a custom range with an end date in the past.
   * `excludeAdmins` excludes users with the Super Admin role.
   */
  getSystemOverview: async (options: { asOfDate?: string; excludeAdmins?: boolean } = {}): Promise<{
    success: boolean;
    data: {
      users: {
        total: number;
        active: number;
        pending: number;
        disabled: number;
        byRole: {
          superAdmin: number;
          admin: number;
          expert: number;
          user: number;
        };
      };
      conversations: {
        total: number;
        active: number;
      };
      messages: {
        total: number;
      };
      escalations: {
        total: number;
        pending: number;
        inProgress: number;
        resolved: number;
      };
      recentActivity: {
        messages: number;
        conversations: number;
        newUsers: number;
        period: string;
      };
    };
    timestamp: string;
  }> => {
    const params = new URLSearchParams();
    if (options.asOfDate) params.set('endDate', options.asOfDate);
    if (options.excludeAdmins) params.set('excludeAdmins', 'true');
    const response = await createAuthenticatedFetch(`/analytics/overview?${params.toString()}`);
    return handleApiResponse(response);
  },

  /**
   * Get feedback analytics
   * GET /api/analytics/feedback
   */
  getFeedbackAnalytics: async (options: AnalyticsRangeOptions = {}): Promise<{
    success: boolean;
    data: {
      summary: {
        totalFeedback: number;
        helpfulCount: number;
        unhelpfulCount: number;
        neutralCount: number;
        helpfulRate: number;
        unhelpfulRate: number;
        feedbackRate: number;
        totalMessages: number;
      };
      distribution: Array<{ type: string; count: number }>;
      trends: Array<{ date: string; feedbackType: string; count: number }>;
      topMessages: Array<{
        messageId: string;
        feedbackCount: number;
        helpfulCount: number;
        unhelpfulCount: number;
        content: string;
      }>;
      topConversations: Array<{
        conversationId: string;
        feedbackCount: number;
        helpfulCount: number;
        title: string;
      }>;
      period: {
        days: number;
        startDate: string;
        endDate: string;
      };
    };
    timestamp: string;
  }> => {
    const response = await createAuthenticatedFetch(`/analytics/feedback?${buildAnalyticsQuery(options)}`);
    return handleApiResponse(response);
  },

  /**
   * Get expert performance analytics
   * GET /api/analytics/experts
   */
  getExpertAnalytics: async (days: number = 30): Promise<{
    success: boolean;
    data: {
      experts: Array<{
        id: string;
        name: string;
        email: string;
        escalationsAssigned: number;
        escalationsResolved: number;
        resolutionRate: number;
        avgResolutionTime: number;
      }>;
      summary: {
        totalExperts: number;
        totalAssigned: number;
        totalResolved: number;
      };
      period: {
        days: number;
        startDate: string;
        endDate: string;
      };
    };
    timestamp: string;
  }> => {
    const response = await createAuthenticatedFetch(`/analytics/experts?days=${days}`);
    return handleApiResponse(response);
  },

  /**
   * Get contact reason analytics for expert escalations
   * GET /api/analytics/contact-reasons
   */
  getContactReasonAnalytics: async (options: AnalyticsRangeOptions = {}): Promise<{
    success: boolean;
    data: {
      distribution: Array<{
        reason: string;
        label: string;
        count: number;
        percentage: number;
      }>;
      trend: Array<{
        date: string;
        reason: string;
        label: string;
        count: number;
      }>;
      summary: {
        totalWithReasons: number;
        totalWithoutReasons: number;
        totalEscalations: number;
        reasonCaptureRate: number;
      };
      period: {
        days: number;
        startDate: string;
        endDate: string;
      };
    };
    timestamp: string;
  }> => {
    const response = await createAuthenticatedFetch(`/analytics/contact-reasons?${buildAnalyticsQuery(options)}`);
    return handleApiResponse(response);
  },

  /**
   * Get a paginated, searchable, sortable per-user analytics table
   * GET /api/analytics/users
   */
  getUserAnalytics: async (options: {
    page?: number;
    limit?: number;
    search?: string;
    sortDir?: 'asc' | 'desc';
    excludeAdmins?: boolean;
    // yyyy-mm-dd — excludes users created after this date (cumulative "as of")
    asOfDate?: string;
  } = {}): Promise<{
    success: boolean;
    data: {
      users: UserAnalyticsRow[];
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
      };
    };
    timestamp: string;
  }> => {
    const params = new URLSearchParams();
    if (options.page) params.set('page', String(options.page));
    if (options.limit) params.set('limit', String(options.limit));
    if (options.search) params.set('search', options.search);
    if (options.sortDir) params.set('sortDir', options.sortDir);
    if (options.excludeAdmins) params.set('excludeAdmins', 'true');
    if (options.asOfDate) params.set('endDate', options.asOfDate);
    const response = await createAuthenticatedFetch(`/analytics/users?${params.toString()}`);
    return handleApiResponse(response);
  },

  // ===== CUSTOMER CONFIG (Admin/Super Admin) =====

  /**
   * Get the current customer config
   * GET /api/admin/customer-config/:customerName
   */
  getCustomerConfig: async (customerName: string): Promise<{
    customerName: string;
    raw: string;
    updatedAt: string;
    updatedBy?: string;
  }> => {
    const response = await createAuthenticatedFetch(`/admin/customer-config/${customerName}`);
    return handleApiResponse(response);
  },

  /**
   * Validate, apply, and save a new customer config
   * PUT /api/admin/customer-config/:customerName
   */
  updateCustomerConfig: async (customerName: string, raw: string): Promise<{
    customerName: string;
    raw: string;
    updatedAt: string;
    updatedBy?: string;
    message: string;
  }> => {
    const response = await createAuthenticatedFetch(`/admin/customer-config/${customerName}`, {
      method: 'PUT',
      body: JSON.stringify({ raw }),
    });
    return handleApiResponse(response);
  },
};

export default api;